import { useEffect, useRef, useState } from 'react'
import { useSupabase } from '../contexts/SupabaseContext'
import { useBusiness } from '../contexts/BusinessContext'
import {
  initPlugin,
  startTracking,
  stopTracking,
  isClockedIn,
  isInitialized,
  isTracking,
  ensureTracking,
  getLocationPermission,
  getPosition,
  onProviderChange,
  updateAuthToken,
  setClockedIn as setPluginClockedIn,
} from '../services/backgroundLocationService'

interface UseResourceLocationSenderOptions {
  updateInterval?: number // unused – kept for interface compat
}

/** Debug status for on-screen banner */
export type LocationDebugStatus =
  | { status: 'inactive'; message: '' }
  | { status: 'checking'; message: string }
  | { status: 'plugin'; message: string }
  | { status: 'error'; message: string }

const INITIAL_DEBUG: LocationDebugStatus = { status: 'inactive', message: '' }
const LOG = '📍 BG-LOC:'

const HEALTH_CHECK_INTERVAL_MS = 60_000

/**
 * Native-only location hook.
 *
 * Effect 1: Initialize the Transistorsoft plugin on sign-in + ALWAYS start
 *           tracking immediately (the edge function gates persistence on
 *           clock-in status server-side, so running the plugin in foreground
 *           is always safe).
 * Effect 2: Handle app foreground/background transitions.
 *           - Foreground  → start tracking + refresh JWT
 *           - Background  → stop tracking (unless clocked in)
 * Effect 3: Refresh plugin auth token when Supabase refreshes JWT.
 * Effect 4: Periodic health check — every 60s verify plugin is alive, check
 *           server clock-in status, and restart if stuck.
 *
 * No web fallback. No visibilitychange listener.
 */
export function useResourceLocationSender(
  _opts: UseResourceLocationSenderOptions = {}
) {
  const supabase = useSupabase()
  const { selectedBusiness } = useBusiness()
  const didInit = useRef(false)
  const [locationDebugStatus, setLocationDebugStatus] = useState<LocationDebugStatus>(INITIAL_DEBUG)

  // Persist the device's location permission to the resources table
  const reportPermission = async (permission: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return
      const { error } = await supabase
        .from('resources')
        .update({
          location_permission: permission,
          location_permission_updated_at: new Date().toISOString(),
        })
        .eq('auth_user_id', user.id)
        .eq('business_id', selectedBusiness?.id)
      if (error) console.error(LOG, '[reportPermission] DB error:', error.message)
      else console.log(LOG, '[reportPermission] Saved:', permission)
    } catch (e) {
      console.error(LOG, '[reportPermission] Exception:', e)
    }
  }

  /**
   * Check the server for an open timeclock entry and sync the local
   * clocked-in flag. This catches remote clock-ins (admin clocks in a
   * resource from the web dashboard) that the device wouldn't know about.
   */
  const syncClockInStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || !selectedBusiness?.id) return

      const { data: resource } = await supabase
        .from('resources')
        .select('id')
        .eq('auth_user_id', user.id)
        .eq('business_id', selectedBusiness.id)
        .maybeSingle()

      if (!resource) return

      const { data: openEntry } = await supabase
        .from('payroll_page')
        .select('id')
        .eq('resource_id', resource.id)
        .eq('business_id', selectedBusiness.id)
        .eq('entry_type', 'timeclock')
        .is('clock_out', null)
        .maybeSingle()

      const serverClockedIn = !!openEntry
      const localClockedIn = isClockedIn()

      if (serverClockedIn !== localClockedIn) {
        console.log(LOG, '[syncClockIn] Mismatch — server:', serverClockedIn, 'local:', localClockedIn, '→ syncing')
        await setPluginClockedIn(serverClockedIn)
      }
    } catch (e) {
      console.warn(LOG, '[syncClockIn] Error:', e)
    }
  }

  /**
   * Write diagnostic info to the resources table so we can monitor the
   * plugin state remotely without needing physical access to the device.
   */
  const reportDiagnostics = async (context: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user || !selectedBusiness?.id) return

      let pluginState: any = null
      try {
        const BG = (await import('@transistorsoft/capacitor-background-geolocation')).default
        pluginState = await BG.getState()
      } catch {}

      let gpsWorking = false
      try {
        const pos = await getPosition(false)
        gpsWorking = !!(pos?.lat && pos?.lng)
      } catch {}

      const info = {
        context,
        ts: new Date().toISOString(),
        initialized: isInitialized(),
        tracking: isTracking(),
        clockedIn_local: isClockedIn(),
        gps_working: gpsWorking,
        plugin_enabled: pluginState?.enabled ?? null,
        plugin_isMoving: pluginState?.isMoving ?? null,
        plugin_didLaunchInBg: pluginState?.didLaunchInBackground ?? null,
        plugin_stopOnTerminate: pluginState?.stopOnTerminate ?? null,
        plugin_url: pluginState?.url ? 'set' : 'missing',
        plugin_hasAuth: pluginState?.headers?.Authorization ? 'yes' : 'no',
        plugin_extras_businessId: pluginState?.extras?.business_id || 'missing',
      }

      console.log(LOG, '[diagnostics]', info)

      await supabase
        .from('resources')
        .update({
          location_debug_info: info,
          location_debug_updated_at: new Date().toISOString(),
        })
        .eq('auth_user_id', user.id)
        .eq('business_id', selectedBusiness.id)
    } catch (e) {
      console.warn(LOG, '[diagnostics] Error:', e)
    }
  }

  // ── Effect 1: Init plugin + start tracking ──
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) {
      if (!didInit.current) setLocationDebugStatus(INITIAL_DEBUG)
      return
    }
    if (didInit.current) return // already initialized

    let cancelled = false
    setLocationDebugStatus({ status: 'checking', message: 'Starting location plugin…' })

    const init = async () => {
      // Only native
      const { Capacitor } = await import('@capacitor/core')
      if (!Capacitor.isNativePlatform()) {
        setLocationDebugStatus({ status: 'error', message: 'Not a native device' })
        return
      }

      // Get auth token
      const { data: { session } } = await supabase.auth.getSession()
      if (cancelled || !session?.access_token) {
        if (!cancelled) setLocationDebugStatus({ status: 'error', message: 'No session token' })
        return
      }

      // Initialize plugin (ready + listeners + HTTP config)
      const ok = await initPlugin({
        businessId: selectedBusiness.id,
        accessToken: session.access_token,
      })
      if (cancelled) return

      if (!ok) {
        setLocationDebugStatus({ status: 'error', message: 'Plugin init failed' })
        return
      }

      didInit.current = true

      // ALWAYS start tracking when in foreground. The edge function gates
      // persistence on server-side clock-in status, so it's safe to run the
      // plugin even when not clocked in — it just means the edge function
      // will skip the insert. This prevents the bug where the plugin never
      // starts because the local _clockedIn flag is stale.
      await startTracking()
      if (cancelled) return

      // Sync clock-in status from the server (catches remote clock-ins)
      await syncClockInStatus()
      if (cancelled) return

      // Report current location permission to the database
      const permission = await getLocationPermission()
      if (permission && !cancelled) {
        reportPermission(permission)
      }

      // Listen for future permission changes (e.g. user toggles in iOS Settings)
      onProviderChange((perm) => {
        reportPermission(perm)
      })

      const mode = isClockedIn() ? 'clocked-in (full background)' : 'foreground-only'
      setLocationDebugStatus({ status: 'plugin', message: `Plugin ready – ${mode}` })
      console.log(LOG, '[hook] Plugin initialized, mode:', mode)

      // Write initial diagnostic snapshot to the database
      await reportDiagnostics('init_complete')
    }

    init().catch(err => {
      if (!cancelled) {
        const msg = err instanceof Error ? err.message : String(err)
        console.error(LOG, '[hook] Init error:', msg)
        setLocationDebugStatus({ status: 'error', message: msg })
      }
    })

    return () => { cancelled = true }
  }, [supabase, selectedBusiness?.id])

  // ── Effect 2: App state transitions (native only) ──
  useEffect(() => {
    let removeListener: (() => void) | null = null

    import('@capacitor/app').then(({ App }) => {
      App.addListener('appStateChange', async ({ isActive }) => {
        if (!isInitialized()) return

        if (isActive) {
          // App came to foreground → force-refresh JWT + kick the plugin
          console.log(LOG, '[appState] Foreground → refreshing token + startTracking')
          try {
            const { data: { session } } = await supabase.auth.refreshSession()
            if (session?.access_token) {
              await updateAuthToken(session.access_token)
            }
          } catch (e) {
            console.warn(LOG, '[appState] Token refresh failed:', e)
          }
          await startTracking()
          // Sync clock-in status from server on every foreground transition
          await syncClockInStatus()
          const perm = await getLocationPermission()
          if (perm) reportPermission(perm)
        } else {
          // App went to background
          if (isClockedIn()) {
            console.log(LOG, '[appState] Background → clocked in, staying active')
          } else {
            console.log(LOG, '[appState] Background → not clocked in, stopping')
            await stopTracking()
          }
        }
      }).then(handle => {
        removeListener = () => handle.remove()
      })
    }).catch(() => {})

    return () => { removeListener?.() }
  }, [])

  // ── Effect 3: Refresh plugin auth token when Supabase refreshes JWT ──
  useEffect(() => {
    if (!supabase) return

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          console.log(LOG, '[auth] Token refreshed — updating plugin headers')
          await updateAuthToken(session.access_token)
        }

        // Also handle SIGNED_IN — when the app restores a session from storage
        // on launch, this fires before TOKEN_REFRESHED. Ensure the plugin has
        // a valid token right away.
        if (event === 'SIGNED_IN' && session?.access_token && isInitialized()) {
          console.log(LOG, '[auth] SIGNED_IN — ensuring plugin has valid token')
          await updateAuthToken(session.access_token)
          if (!isTracking()) {
            await startTracking()
          }
        }
      }
    )

    return () => { subscription.unsubscribe() }
  }, [supabase])

  // ── Effect 4: Periodic health check ──
  // Every 60s, verify the plugin is actually running, check server clock-in
  // status, and restart tracking if stuck.
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return

    const interval = setInterval(async () => {
      if (!isInitialized()) return

      console.log(LOG, '[healthCheck] Running periodic check')

      // Verify plugin is alive
      const healthy = await ensureTracking()
      if (!healthy) {
        console.warn(LOG, '[healthCheck] Plugin not healthy — attempting restart')
        await startTracking()
      }

      // Sync clock-in status from server
      await syncClockInStatus()

      // Proactively refresh the JWT if it's getting close to expiry
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.access_token) {
          await updateAuthToken(session.access_token)
        }
      } catch {}

      // Write diagnostic snapshot so we can monitor remotely
      await reportDiagnostics('health_check')
    }, HEALTH_CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [supabase, selectedBusiness?.id])

  return { locationDebugStatus }
}
